import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import type { IOrgMember } from './OrgMembersPage';
import { humanize } from './OnboardingFieldsDrawer';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { useIsMobileApp } from '../../../../mobile/mobileApp';
import { MOBILE_CARD_SX, mobileValueSx } from './mobileMemberParts';

interface PendingProfilesViewProps {
  members: IOrgMember[];
  requiredFieldKeys: string[];
  onNudge: (member: IOrgMember) => void;
}

const headerCellSx = {
  fontSize: 12,
  fontWeight: 600,
  color: '#868686',
  fontFamily: 'Inter, sans-serif',
  textTransform: 'uppercase' as const,
  whiteSpace: 'nowrap' as const
};

const FIELD_COL_WIDTH = 110;
const NUDGE_COL_WIDTH = 100;

/**
 * Columns are dynamic — one per org-required onboarding field — so each org
 * sees exactly the fields it has marked required, not a fixed set.
 */
const PendingProfilesView = ({ members, requiredFieldKeys, onNudge }: PendingProfilesViewProps) => {
  const isMobileApp = useIsMobileApp();
  // A phone: a card per member, each required field a tick or a cross with its name, then Nudge.
  if (isMobileApp) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {members.length === 0 ? (
          <Typography
            sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 14, textAlign: 'center', py: 6 }}
          >
            No incomplete profiles
          </Typography>
        ) : (
          members.map((member) => {
            const missing = new Set(member.missingFields || []);
            return (
              <Box key={member.id} sx={MOBILE_CARD_SX}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Typography sx={{ ...mobileValueSx(), flex: 1, minWidth: 0, fontSize: 15 }}>
                    {member.fullName}
                  </Typography>
                  <DosenButton
                    tone="cta"
                    sizeVariant="sm"
                    aria-label={`Nudge ${member.fullName}`}
                    onClick={() => onNudge(member)}
                  >
                    Nudge
                  </DosenButton>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px' }}>
                  {requiredFieldKeys.map((key) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {missing.has(key) ? (
                        <CancelIcon sx={{ fontSize: 16, color: '#F44336' }} />
                      ) : (
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#3BB273' }} />
                      )}
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#B6B6B6' }}>
                        {humanize(key)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    );
  }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: '15px',
          alignItems: 'center',
          px: '25px',
          py: '16px',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: '#1C1C1C',
          borderBottom: '1px solid #2A2A2A'
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={headerCellSx}>Name</Typography>
        </Box>
        {requiredFieldKeys.map((key) => (
          <Box key={key} sx={{ width: FIELD_COL_WIDTH, flexShrink: 0, textAlign: 'center' }}>
            <Typography sx={headerCellSx}>{humanize(key)}</Typography>
          </Box>
        ))}
        <Box sx={{ width: NUDGE_COL_WIDTH, flexShrink: 0 }} />
      </Box>

      {members.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography sx={{ color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
            No incomplete profiles
          </Typography>
        </Box>
      ) : (
        members.map((member) => {
          const missing = new Set(member.missingFields || []);
          return (
            <Box
              key={member.id}
              sx={{
                display: 'flex',
                gap: '15px',
                alignItems: 'center',
                px: '25px',
                py: '15px',
                borderTop: '1px solid #1C1C1C'
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#FDFDFD',
                    fontFamily: 'Inter, sans-serif',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {member.fullName}
                </Typography>
              </Box>
              {requiredFieldKeys.map((key) => (
                <Box
                  key={key}
                  sx={{ width: FIELD_COL_WIDTH, flexShrink: 0, display: 'flex', justifyContent: 'center' }}
                >
                  {missing.has(key) ? (
                    <CancelIcon sx={{ fontSize: 18, color: '#F44336' }} />
                  ) : (
                    <CheckCircleIcon sx={{ fontSize: 18, color: '#3BB273' }} />
                  )}
                </Box>
              ))}
              <Box sx={{ width: NUDGE_COL_WIDTH, flexShrink: 0 }}>
                <DosenButton
                  tone="cta"
                  sizeVariant="xs"
                  aria-label={`Nudge ${member.fullName}`}
                  onClick={() => onNudge(member)}
                >
                  Nudge
                </DosenButton>
              </Box>
            </Box>
          );
        })
      )}
    </>
  );
};

export default PendingProfilesView;
